import { useState, useEffect, useRef, Fragment } from "react";
import { Target, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { toast } from "../../../utils/notify";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { IMPACT_OPTIONS, LIKELIHOOD_OPTIONS, RISK_RESPONSE_OPTIONS } from "../../../constants/AppEnums";
import { updateCustomerRiskApi, getRiskEvaluationListApi } from "../../../services/axios/AuthApis/RisksManagementApis";
import { CloudArrowUpIcon, ShieldExclamationIcon } from "@heroicons/react/24/outline";
import { DataTable } from "../../common/DataTable";
import { useSelector } from "react-redux";

// Fully independent of Risk Register/Risk Analysis - own state, own
// fetch function, own backend endpoint (getRiskEvaluationListApi /
// get_risk_evaluation_list), same isolation reasoning as Risk Analysis.
export const RiskEvaluation = () => {
  const userDetail = useSelector((state) => state.user);
  const selectedCompany = useSelector((state) => state.company.selectedCompany);
  const userRole = userDetail?.user?.role || '';
  const disable = userRole == "auditor";
  const customerId = (userRole === 'security_analyst' || userRole === 'auditor')
    ? selectedCompany?.user_id
    : userDetail?.user?.id;

  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [remarks, setRemarks] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [totalRiskCount, setTotalRiskCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [textColumnFilters, setTextColumnFilters] = useState({});
  const requestIdRef = useRef(0);

  const totalPages = Math.max(1, Math.ceil(totalRiskCount / itemsPerPage));
  const columnFilters = Object.entries(textColumnFilters || {})
    .filter(([, value]) => value)
    .map(([id, value]) => ({ id, value }));

  const fetchRisks = async (page = currentPage) => {
    if (!customerId) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const response = await getRiskEvaluationListApi(customerId, page, itemsPerPage, columnFilters);
      if (requestId !== requestIdRef.current) return;
      if (response.success) {
        setRisks(response.data || []);
        setTotalRiskCount(response.total_count || 0);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("Failed to load risks", err);
      toast.error("Failed to load risks");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchRisks(1);
  }, [customerId, itemsPerPage, JSON.stringify(columnFilters)]);

  const handlePageChange = (page, pageSize) => {
    if (pageSize !== undefined && pageSize !== itemsPerPage) {
      setItemsPerPage(pageSize);
      setCurrentPage(1);
      return;
    }
    if (page >= 1 && page <= totalPages) {
      fetchRisks(page);
    }
  };

  const getScoreFromValue = (options, value) => {
    return options.find(opt => opt.value === value)?.score || 0;
  }

  const calculateResidualRating = (impact, likelihood) => {
    if (!impact || !likelihood) return "";
    const impactScore = getScoreFromValue(IMPACT_OPTIONS, impact);
    const likelihoodScore = getScoreFromValue(LIKELIHOOD_OPTIONS, likelihood);
    const total = impactScore + likelihoodScore;

    if (total >= 9) return "Critical (9-10)";
    if (total >= 7) return "High (8)";
    if (total >= 6) return "Significant (6-7)";
    if (total >= 4) return "Moderate (4-5)";
    return "Low (2-3)";
  };

  const updateRisk = async (riskId, updates) => {
    const risk = risks.find(r => r.id === riskId);
    if (!risk) return;

    setSaving(prev => ({ ...prev, [riskId]: true }));

    let newResidualImpact = updates.residual_impact_type ?? risk.residual_impact_type;
    let newResidualLikelihood = updates.residual_likelihood_type ?? risk.residual_likelihood_type;
    let newResidualRating = calculateResidualRating(newResidualImpact, newResidualLikelihood);
    if (updates.risk_response == "Accept") {
      newResidualImpact = "insignificant";
      newResidualLikelihood = "rare";
      newResidualRating = calculateResidualRating(newResidualImpact, newResidualLikelihood);
    }
    try {
      const updateData = {
        ...risk,
        ...updates,
        residual_impact_type: newResidualImpact,
        residual_likelihood_type: newResidualLikelihood,
        residual_risk_score: newResidualRating || risk.residual_risk_rating
      };
      const response = await updateCustomerRiskApi(updateData, risk.id);
      if (response.success) {
        setRisks(updateData => risks.map(r => r.id === riskId ? { ...r, ...updateData } : r));
        toast.success(
          `Risk ${risk.risk_id} updated successfully.`,
        );
        fetchRisks(currentPage);
      }
    } catch (err) {
      console.log({ err })
      toast.error("Failed to update risk");
    } finally {
      setSaving(prev => ({ ...prev, [riskId]: false }));
    }
  };

  const saveRemarks = async (riskId) => {
    const risk = risks.find(r => r.id === riskId);
    if (!risk) return;

    const remarkText = (remarks[riskId] ?? risk.evaluation_remarks ?? "").trim();

    // Mandatory remarks only for "Accept" response
    if (risk.risk_response === 'Accept' && !remarkText) {
      toast.error("Remarks are required when risk response is Accept.");
      return;
    }

    setSaving(prev => ({ ...prev, [`remarks_${riskId}`]: true }));

    try {
      const formData = new FormData();
      formData.append('risk_response', risk.risk_response || "Reduce");
      formData.append('evaluation_remarks', remarkText);
      formData.append('employee_id', userDetail?.user?.id || '');

      // Append file if present
      const uploaded = uploadedFiles[riskId];
      if (uploaded?.file) {
        formData.append('evaluationFile', uploaded.file);
      }

      // Pass FormData as the first argument; your API function should detect it
      const response = await updateCustomerRiskApi(formData, risk.id);

      if (response.success) {
        toast.success(`Risk ${risk.risk_id} remarks saved successfully.`);

        // Clean up preview URL and reset state
        if (uploaded?.previewUrl) {
          URL.revokeObjectURL(uploaded.previewUrl);
        }
        setUploadedFiles(prev => {
          const newState = { ...prev };
          delete newState[riskId];
          return newState;
        });
        setRemarks(prev => ({ ...prev, [riskId]: '' }));
        setExpandedRow(null);
        fetchRisks(currentPage);
      }
    } catch (err) {
      console.error("Failed to save remarks", err);
      toast.error("Failed to save remarks");
    } finally {
      setSaving(prev => ({ ...prev, [`remarks_${riskId}`]: false }));
    }
  };

  const getRatingClass = (rating) => {
    if (!rating) return "bg-gray-100 text-gray-700";
    if (rating.includes("critical")) return "bg-red-500 text-white";
    if (rating.includes("high")) return "bg-orange-500 text-white";
    if (rating.includes("significant")) return "bg-amber-500 text-white";
    if (rating.includes("moderate")) return "bg-yellow-400 text-yellow-900";
    return "bg-green-500 text-white";
  };

  const getResponseColor = (response) => {
    switch (response) {
      case "Accept": return "bg-green-100 text-green-700 border-green-200";
      case "Reduce": return "bg-blue-100 text-blue-700 border-blue-200";
      case "Transfer": return "bg-purple-100 text-purple-700 border-purple-200";
      case "Avoid": return "bg-red-100 text-red-700 border-red-200";
      default: return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const columns = [
    {
      accessorKey: "risk_id",
      header: "Risk ID",
      size: 128,
      meta: { filter: { variant: "text", label: "Risk ID", placeholder: "Filter risk ID" } },
      cell: ({ row }) => (
        <span className="font-mono text-sm font-medium text-blue-600">{row.original.risk_id}</span>
      ),
    },
    {
      id: "inherent_rating",
      accessorFn: (risk) => risk.inherent_risk_score,
      header: "Inherent Rating",
      enableSorting: false,
      cell: ({ row }) => (
        <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getRatingClass(row.original?.inherent_risk_rating?.toLowerCase())}`}>
          {row.original.inherent_risk_score}
        </span>
      ),
    },
    {
      id: "risk_response",
      accessorFn: (risk) => risk.risk_response,
      header: "Response",
      enableSorting: false,
      cell: ({ row }) => {
        const risk = row.original;
        return (
          <Select
            value={risk.risk_response || "Reduce"}
            onValueChange={(val) => handleRiskResponseChange(risk.id, val)}
            disabled={saving[risk.id] || disable}
          >
            <SelectTrigger className={`w-full max-w-[140px] border-2 ${getResponseColor(risk.risk_response)} rounded-md`} data-testid={`response-${risk.risk_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              {RISK_RESPONSE_OPTIONS.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      id: "residual_impact",
      accessorFn: (risk) => risk.residual_impact_type,
      header: "Residual Impact",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Residual Impact", options: IMPACT_OPTIONS } },
      cell: ({ row }) => {
        const risk = row.original;
        return (
          <Select
            value={risk.residual_impact_type || "none"}
            onValueChange={(val) => updateRisk(risk.id, { residual_impact_type: val === "none" ? "" : val })}
            disabled={saving[risk.id] || disable}
          >
            <SelectTrigger className="w-full max-w-[160px] bg-white border border-slate-300 rounded-md" data-testid={`residual-impact-${risk.risk_id}`}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              {IMPACT_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      id: "residual_likelihood",
      accessorFn: (risk) => risk.residual_likelihood_type,
      header: "Residual Likelihood",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Residual Likelihood", options: LIKELIHOOD_OPTIONS } },
      cell: ({ row }) => {
        const risk = row.original;
        return (
          <Select
            value={risk.residual_likelihood_type || "none"}
            onValueChange={(val) => updateRisk(risk.id, { residual_likelihood_type: val === "none" ? "" : val })}
            disabled={saving[risk.id] || disable}
          >
            <SelectTrigger className="w-full max-w-[160px] bg-white border border-slate-300 rounded-md" data-testid={`residual-likelihood-${risk.risk_id}`}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              {LIKELIHOOD_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      id: "residual_rating",
      accessorFn: (risk) => risk.residual_risk_score,
      header: "Residual Rating",
      enableSorting: false,
      cell: ({ row }) => (
        <span className={`inline-block w-40 text-center px-3 py-1.5 rounded-full text-xs font-semibold ${getRatingClass(row.original?.residual_risk_score?.toLowerCase())}`}>
          {row.original.residual_risk_score || "Not Assessed"}
        </span>
      ),
    },
    {
      id: "remarks",
      header: "Remarks",
      enableSorting: false,
      size: 340,
      cell: ({ row }) => {
        const risk = row.original;
        const isOpen = expandedRow === risk.id;
        return (
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExpandedRow(isOpen ? null : risk.id)}
              className="text-slate-600 hover:text-white hover:bg-white bg-blue-600 text-white"
            >
              {isOpen ? "Hide" : "Add/View"}
            </Button>

            {isOpen && (
              <div className="mt-3 space-y-3 w-80 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">
                    Evaluation Remarks / Justification
                    <span className="text-red-500"> *</span>
                    <span className="text-xs text-gray-500 ml-2 font-normal">(For risk: {risk.risk_id})</span>
                  </label>
                  {risk.risk_response == "Accept" && userRole != "auditor" && (
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor={`evaluation-upload-${risk.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-blue-200 text-blue-600 hover:text-blue-900 cursor-pointer"
                        title="Upload file"
                      >
                        <CloudArrowUpIcon className="w-4 h-4" />
                        Upload
                      </label>
                      <input
                        id={`evaluation-upload-${risk.id}`}
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={(event) => handleUploadFile(risk.id, event)}
                      />
                      {uploadedFiles[risk.id] && (
                        <a
                          href={uploadedFiles[risk.id].previewUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-900 underline truncate max-w-[220px]"
                          title={uploadedFiles[risk.id].name}
                        >
                          {uploadedFiles[risk.id].name}
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <Textarea
                  value={remarks[risk.id] ?? risk.evaluation_remarks ?? ""}
                  onChange={(e) => setRemarks(prev => ({ ...prev, [risk.id]: e.target.value }))}
                  placeholder="Add evaluation justification, response rationale, or notes..."
                  className="bg-white border-slate-300 min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid={`evaluation-remarks-${risk.risk_id}`}
                  disabled={disable}
                />
                {userRole != "auditor" && (
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" size="sm" onClick={() => setExpandedRow(null)} className="border-slate-300">
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => saveRemarks(risk.id)}
                      disabled={saving[`remarks_${risk.id}`]}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Save className="w-4 h-4 mr-1" />
                      {saving[`remarks_${risk.id}`] ? "Saving..." : "Save Remarks"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      },
    },
  ];
  const handleRiskResponseChange = (riskId, response) => {
    setRisks(prev => prev.map(risk => {
      if (risk.id !== riskId) return risk;

      let updatedRisk = { ...risk, risk_response: response };
      if (response === "Accept") {
        const residualImpact = "insignificant";
        const residualLikelihood = "rare";
        updatedRisk = {
          ...updatedRisk,
          residual_impact_type: residualImpact,
          residual_likelihood_type: residualLikelihood,
          residual_risk_score: calculateResidualRating(residualImpact, residualLikelihood)
        };
      }
      return updatedRisk;
    }));

    // Keep remarks panel open to make mandatory remark entry obvious.
    setExpandedRow(riskId);
  }
  const handleUploadFile = (riskId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadedFiles(prev => {
      // Revoke previous preview URL to avoid memory leaks
      if (prev[riskId]?.previewUrl) {
        URL.revokeObjectURL(prev[riskId].previewUrl);
      }
      return {
        ...prev,
        [riskId]: {
          file,
          previewUrl: URL.createObjectURL(file),
          name: file.name,
          type: file.type
        }
      };
    });

    // Allow re-uploading the same file
    event.target.value = "";
  };
  return (
    <div className="space-y-6">
      {/* Response Options */}
      <Card className="card-surface mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-500" />
            Risk Response Options
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-lg bg-green-50 border border-green-200">
              <p className="font-semibold text-green-700">Accept</p>
              <p className="text-xs text-green-600 mt-1">Risk within tolerance</p>
            </div>
            <div className="p-3 rounded-lg border border-blue-200">
              <p className="font-semibold text-blue-700">Reduce</p>
              <p className="text-xs text-blue-600 mt-1">Implement controls</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
              <p className="font-semibold text-purple-700">Transfer</p>
              <p className="text-xs text-purple-600 mt-1">Share with third party</p>
            </div>
            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="font-semibold text-red-700">Avoid</p>
              <p className="text-xs text-red-600 mt-1">Eliminate risk source</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Risk Evaluation Table */}
      <Card className="card-surface">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-800">
            Risk Evaluation & Residual Assessment
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            data={risks}
            getRowId={(risk) => String(risk.id)}
            loading={loading}
            maxBodyHeight="65vh"
            emptyMessage={(
              <div className="flex flex-col items-center justify-center text-gray-400">
                <ShieldExclamationIcon className="mb-3 h-14 w-14 opacity-40" />
                <p className="text-lg font-medium">No risks with inherent assessment</p>
                <p className="mt-1 text-sm">Complete Risk Analysis first.</p>
              </div>
            )}
            manualPagination
            rowCount={totalRiskCount}
            pagination={{ pageIndex: currentPage - 1, pageSize: itemsPerPage }}
            onPaginationChange={(updater) => {
              const currentPagination = { pageIndex: currentPage - 1, pageSize: itemsPerPage };
              const nextPagination = typeof updater === 'function'
                ? updater(currentPagination)
                : updater;
              handlePageChange(nextPagination.pageIndex + 1, nextPagination.pageSize);
            }}
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            manualFiltering
            columnFilters={columnFilters}
            onColumnFiltersChange={(updater) => {
              const nextFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
              const values = Object.fromEntries(nextFilters.map(({ id, value }) => [id, value]));
              const textFilters = Object.fromEntries(
                ['risk_id', 'residual_impact', 'residual_likelihood']
                  .filter((id) => values[id])
                  .map((id) => [id, values[id]]),
              );
              setTextColumnFilters(textFilters);
              setCurrentPage(1);
            }}
            className="overflow-hidden rounded-xl shadow-2xl"
          />
        </CardContent>
      </Card>
    </div>
  );
};